import { useState, useRef, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Input } from "../components/ui/input";
import {
  Upload,
  X,
  AlertCircle,
  CheckCircle,
  Sparkles,
  FileSpreadsheet,
  Zap,
  ArrowRight,
  Trash2,
  Plus,
  Globe,
  Calendar,
  Banknote,
} from "lucide-react";
import { cn } from "../lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import { useTheme } from "../contexts/theme-provider";
import { getAllOrgBySector } from "../services/organizations";
import { SearchableSelect } from "./searchable-select";
import { Label } from "@radix-ui/react-label";
import {
  getAllCurrenciesByCountry,
  getAllCurrencyConvRate,
} from "../services/lookup";

interface RowData {
  id: string;
  reportName: string;
  country: string;
  bank: string;
  currency: string;
  rate: any;
  file: File | null;
  isValid: boolean;
  validationError?: string;
  banks: any;
  rates: any;
  currencies: any;
  loadingBanks: boolean;
}

interface BulkFileUploadProps {
  sector: any;
  subSector: any;
  countries: any[];
  currencies: any[];
  onStartBulkAnalysis: (filesData: any[]) => void;
  isProcessing: boolean;
  error: string | null;
  accessToken: string;
}

const MAX_ROWS = 10;

export function BulkFileUpload({
  sector,
  subSector,
  countries,
  onStartBulkAnalysis,
  isProcessing,
  error,
  accessToken,
}: BulkFileUploadProps) {
  const { theme } = useTheme();
  const [rows, setRows] = useState<RowData[]>([]);
  const [validationError, setValidationError] = useState<string>("");
  const fileInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const [isSingleCountry, setIsSingleCountry] = useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [currencies, setCurrencies] = useState<any>([]);
  const [selectedCurrency, setSelectedCurrency] = useState("all");
  const [selectedCurrencyRate, setSelectedCurrencyRate] = useState("");
  const [currencyRates, setCurrencyRates] = useState<any>([]);

  useEffect(() => {
    if (rows.length === 0) {
      addRow();
    }
  }, []);

  useEffect(() => {
    if (!accessToken) return;

    const fetchCurrencyRates = async () => {
      try {
        const currencyRates = await getAllCurrencyConvRate(
          accessToken,
          selectedCountry,
          selectedCurrency,
        );
        setCurrencyRates(currencyRates);
      } catch (error) {
        console.error("Failed to fetch currencies:", error);
      }
    };

    if (selectedCurrency !== "all" && selectedCountry !== "all")
      fetchCurrencyRates();
  }, [selectedCurrency, selectedCountry, accessToken]);

  useEffect(() => {
    if (!accessToken) return;

    if (selectedCountry !== "all") {
      handleCountryChange(selectedCountry, null);
    }
  }, [selectedCountry, accessToken]);

  useEffect(() => {
    if (!accessToken) return;

    // When switching to single country mode, update all rows with banks
    if (isSingleCountry && selectedCountry !== "all") {
      handleCountryChange(selectedCountry, null);
    }
  }, [isSingleCountry, accessToken]);

  useEffect(() => {
    if (!accessToken) return;

    if (selectedCurrency !== "all") {
      handleCurrencyChange(selectedCurrency, selectedCountry);
    }
  }, [selectedCurrency, accessToken]);

  useEffect(() => {
    if (!accessToken) return;

    if (selectedCurrencyRate !== "") {
      handleCurrencyRateChange(selectedCurrencyRate);
    }
  }, [selectedCurrencyRate, accessToken]);

  const generateId = () => Math.random().toString(36).substring(2, 9);

  const addRow = async () => {
    if (rows.length >= MAX_ROWS) {
      setValidationError(`Maximum ${MAX_ROWS} rows allowed`);
      return;
    }

    try {
      if (isSingleCountry) {
        const newRow: RowData = {
          id: generateId(),
          reportName: "",
          country: selectedCountry,
          bank: "",
          currency: selectedCurrency,
          rate: selectedCurrencyRate !== "" ? selectedCurrencyRate : "na",
          file: null,
          isValid: true,
          banks: [],
          rates: [],
          currencies: currencies,
          loadingBanks: false,
        };

        if (selectedCountry !== "all") {
          const response = await getAllOrgBySector(
            accessToken,
            selectedCountry,
            sector?.id,
            subSector?.id,
            true,
          );

          const formattedBanks = response?.map((org: any) => ({
            label: org.name,
            value: org.id,
            iso2: org.country_code,
            iso3: org.country_code_iso3 || org.country_code_iso3,
          }));

          newRow.banks = formattedBanks;
        }

        if (selectedCurrency !== "all") {
          // Check if selected currency is local currency
          const localCurrency = currencies?.find(
            (c: { iso2: string }) => c.iso2 === selectedCountry
          )?.value;

          if (selectedCurrency !== localCurrency) {
            try {
              const response = await getAllCurrencyConvRate(
                accessToken,
                selectedCountry,
                selectedCurrency,
              );

              const rates = response?.map((rate: any) => ({
                label: rate.conversion_date,
                value: rate.id,
              }));

              newRow.rates = rates;
            } catch (error) {
              newRow.rates = []
            }
          } else {
            // Local currency selected, no conversion rates needed
            newRow.rates = [];
            newRow.rate = "na";
          }
        }

        setRows([...rows, newRow]);
      } else {
        const newRow: RowData = {
          id: generateId(),
          reportName: "",
          country: "",
          bank: "",
          currency: "",
          rate: undefined,
          file: null,
          isValid: true,
          banks: [],
          rates: [],
          currencies: [],
          loadingBanks: false,
        };
        setRows([...rows, newRow]);
      }
    } catch (error) {
      console.log(error);
    }
    setValidationError("");
  };

  const removeRow = (id: string) => {
    setRows(rows.filter((row) => row.id !== id));
    fileInputRefs.current.delete(id);
  };

  const updateRowField = <K extends keyof RowData>(
    id: string,
    field: K,
    value: RowData[K],
  ) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  const handleCountryChange = async (country: string, id?: string | null) => {
    if (!accessToken) return;

    // ✅ SINGLE COUNTRY MODE
    if (isSingleCountry && !id) {
      try {
        const [banksResponse, currenciesResponse] = await Promise.all([
          getAllOrgBySector(accessToken, country, sector?.id, subSector?.id, true),
          getAllCurrenciesByCountry(accessToken, country),
        ]);

        const formattedBanks = banksResponse?.map((org: any) => ({
          label: org.name,
          value: org.id,
          iso2: org.country_code,
          iso3: org.country_code_iso3 || org.country_code_iso3,
        }));

        const formattedCurrencies = currenciesResponse?.map((c: any) => ({
          label: c.currency + " - " + c.currency_name + " - " + c.currency_symbol,
          value: c.currency,
          iso2: c.iso2,
        }));

        setCurrencies(formattedCurrencies);

        setRows((prev) =>
          prev.map((row) => ({
            ...row,
            country: country,
            currency: "",
            banks: formattedBanks,
            currencies: formattedCurrencies,
            loadingBanks: false,
          })),
        );
      } catch (error) {
        console.error("Failed to fetch country data", error);

        setRows((prev) =>
          prev.map((row) => ({
            ...row,
            currencies: [],
            banks: [],
            loadingBanks: false,
          })),
        );

        setCurrencies([]);
      }

      return;
    }

    // ✅ MULTI COUNTRY MODE (per row)
    if (!isSingleCountry && id) {
      updateRowField(id, "country", country);
      updateRowField(id, "currencies", []);
      updateRowField(id, "currency", "");
      updateRowField(id, "bank", "");
      updateRowField(id, "banks", []);
      updateRowField(id, "loadingBanks", true);

      if (!country) {
        updateRowField(id, "loadingBanks", false);
        return;
      }

      try {
        const response = await getAllOrgBySector(
          accessToken,
          country,
          sector?.id,
          subSector?.id,
          true, // Get all organizations for bulk upload
        );

        const formattedBanks = response?.map((org: any) => ({
          label: org.name,
          value: org.id,
          iso2: org.country_code,
          iso3: org.country_code_iso3 || org.country_code_iso3,
        }));

        updateRowField(id, "banks", formattedBanks);

        try {
          const currenciesResponse = await getAllCurrenciesByCountry(
            accessToken,
            country,
          );
          const formattedCurrencies = currenciesResponse?.map((c: any) => ({
            label: c.currency + " - " + c.currency_name + " - " + c.currency_symbol,
            value: c.currency,
            iso2: c.iso2,
          }));

          updateRowField(id, "currencies", formattedCurrencies);
        } catch (error) {
          updateRowField(id, "currencies", []);
          console.error("Failed to fetch currencies:", error);
        }

      } catch (err) {
        console.error("Failed to fetch banks for row", err);
      } finally {
        updateRowField(id, "loadingBanks", false);
      }
    }
  };

  const handleCurrencyChange = async (
    currency: string,
    country: string,
    id?: string | null,
  ) => {
    if (!accessToken) return;

    // ✅ SINGLE COUNTRY MODE
    if (isSingleCountry && !id) {
      setSelectedCurrency(currency);

      // Check if selected currency is local currency
      const localCurrency = currencies?.find(
        (c: { iso2: string }) => c.iso2 === country
      )?.value;

      if (currency !== localCurrency) {
        try {
          const rates = await getAllCurrencyConvRate(
            accessToken,
            country,
            currency,
          );
          setCurrencyRates(rates);

          const formattedRates = rates?.map((rate: any) => ({
            label: rate.conversion_date,
            value: rate.id,
          }));

          // Auto-select the latest rate (first in the list)
          const latestRateId = rates && rates.length > 0 ? String(rates[0].id) : "";
          if (latestRateId) {
            setSelectedCurrencyRate(latestRateId);
          }

          // updateRowField(rows[row]?.id, "rates", formattedRates);
          // Update all rows currency
          setRows((prev) =>
            prev.map((row) => ({
              ...row,
              rates: formattedRates,
              currency: currency,
              rate: latestRateId || row.rate,
            })),
          );
        } catch (err: any) {
          const status = err?.status || err?.response?.status;

          if (status === 404) {
            const formattedRates = [
              {
                label: "Select currency date",
                value: "na",
              },
            ];
          
            setRows((prev) =>
              prev.map((row) => ({
                ...row,
                currency: currency,
                rates: formattedRates,
                rate: "na"
              })),
            );

            setSelectedCurrencyRate("")
          } else {
            setRows((prev) =>
              prev.map((row) => ({
                ...row,
                currency: currency,
              })),
            );
          }
          
          console.error("Failed to fetch currency rates", err);
        }
      } else {
        // Local currency selected, no conversion rates needed
        setCurrencyRates([]);
        setSelectedCurrencyRate("");
        setRows((prev) =>
          prev.map((row) => ({
            ...row,
            currency: currency,
            rates: [],
            rate: "na"
          })),
        );
      }

      return;
    }

    // ✅ MULTI COUNTRY MODE (per row)
    if (!isSingleCountry && id) {
      updateRowField(id, "currency", currency);

      // Find the row to get its currencies
      const row = rows.find((r) => r.id === id);
      
      // Check if selected currency is local currency
      const localCurrency = row?.currencies?.find(
        (c: { iso2: string }) => c.iso2 === country
      )?.value;

      if (currency !== localCurrency) {
        try {
          const rates = await getAllCurrencyConvRate(
            accessToken,
            country,
            currency,
          );
          const formattedRates = rates?.map((rate: any) => ({
            label: rate.conversion_date,
            value: rate.id,
          }));
          // Optional: store rates per row if needed
          updateRowField(id, "rates", formattedRates);
          // Auto-select the latest rate (first in the list)
          if (rates && rates.length > 0) {
            updateRowField(id, "rate", String(rates[0].id));
          }
        } catch (err: any) {
          const status = err?.status || err?.response?.status;

          if (status === 404) {
            const formattedRates = [
              {
                label: "Select currency date",
                value: "na",
              },
            ];

            updateRowField(id, "rate", "na");
            updateRowField(id, "rates", formattedRates);
          }
        }
      } else {
        // Local currency selected, no conversion rates needed
        updateRowField(id, "rates", []);
        updateRowField(id, "rate", "na");
      }
    }
  };

  const handleCurrencyRateChange = (rate: string, id?: string | null) => {
    // ✅ SINGLE COUNTRY MODE → update all rows
    if (isSingleCountry && !id) {
      setSelectedCurrencyRate(rate);

      setRows((prev) =>
        prev.map((row) => ({
          ...row,
          rate: rate,
        })),
      );

      return;
    }

    // ✅ MULTI COUNTRY MODE → update only one row
    if (!isSingleCountry && id) {
      updateRowField(id, "rate", rate);
    }
  };

  const validateExcelFile = async (
    file: File,
  ): Promise<{ isValid: boolean; error?: string }> => {
    try {
      const validExtensions = [".xlsx"];
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));

      if (!validExtensions.includes(fileExtension)) {
        return {
          isValid: false,
          error: "Invalid file format. Please upload .xlsx only",
        };
      }

      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        return { isValid: false, error: "File size exceeds 10MB limit" };
      }

      if (file.size < 1024) {
        return {
          isValid: false,
          error: "File appears to be empty or corrupted",
        };
      }

      if (
        sector.name === "FI" &&
        (subSector.name === "Commercial Banks" ||
          subSector.name === "Development Financial Institutions")
      ) {
        const requiredSheets = [
          "Key Financials",
          "Balance Sheet",
          "Income Statement",
          "Ratios",
          "Off Balance Sheet",
        ];

        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheetNames = workbook.SheetNames;

        const missingSheets = requiredSheets.filter(
          (sheet) => !sheetNames.includes(sheet),
        );

        if (missingSheets.length > 0) {
          return {
            isValid: false,
            error: `Missing required sheets: ${missingSheets.join(", ")}`,
          };
        }
      }

      return { isValid: true };
    } catch (err) {
      return {
        isValid: false,
        error: "An error occurred while validating the file",
      };
    }
  };

  const handleFileSelect = async (id: string, file: File | null) => {
    if (!file) {
      updateRowField(id, "file", null);
      updateRowField(id, "isValid", true);
      updateRowField(id, "validationError", undefined);
      return;
    }

    const validation = await validateExcelFile(file);
    updateRowField(id, "file", file);
    updateRowField(id, "isValid", validation.isValid);
    updateRowField(id, "validationError", validation.error);
  };

  const triggerFileInput = (id: string) => {
    fileInputRefs.current.get(id)?.click();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (rows.length === 0) {
      setValidationError("Please add at least one file to upload");
      return;
    }

    const invalidRows = rows.filter(
      (row) =>
        !row.file ||
        !row.reportName.trim() ||
        !row.country ||
        !row.bank ||
        !row.currency ||
        !row.isValid,
    );

    // For rows with local currency, rate is not required
    const invalidRowsWithoutRate = rows.filter(
      (row) =>
        !row.file ||
        !row.reportName.trim() ||
        !row.country ||
        !row.bank ||
        !row.currency ||
        !row.isValid,
    ).filter((row) => {
      const localCurrency = row.currencies?.find(
        (c: { iso2: string }) => c.iso2 === row.country
      )?.value;
      // If not local currency, rate is required
      if (localCurrency !== row.currency) {
        return !row.rate;
      }
      return false;
    });

    if (invalidRows.length > 0 || invalidRowsWithoutRate.length > 0) {
      setValidationError(
        `Please complete all required fields and ensure files are valid (${invalidRows.length + invalidRowsWithoutRate.length} row(s) incomplete)`,
      );
      return;
    }
console.log(rows)
    const filesData = rows.map((row) => ({
      file: row.file,
      reportName: row.reportName,
      organization: row.bank,
      country: isSingleCountry ? selectedCountry : row.country,
      currency: isSingleCountry ? selectedCurrency : row.currency,
      selectedRate: isSingleCountry
        ? selectedCurrencyRate
        : row.rate
          ? row.rate
          : null,
    }));

    onStartBulkAnalysis(filesData);
  };

  const isDateDisabled = (countryCode: string, currencyCode: string, rowCurrencies: any[]) => {
    if (!countryCode || !currencyCode) return true;

    // Find local currency from currencies data (the one matching the country's iso2)
    const localCurrency = rowCurrencies?.find(
      (c: { iso2: string }) => c.iso2 === countryCode
    )?.value;

    return localCurrency === currencyCode;
  };

  const allRowsValid =
    rows.length > 0 &&
    rows.every(
      (row) =>
        row.file &&
        row.reportName.trim() &&
        row.country &&
        row.bank &&
        row.currency &&
        row.rate &&
        row.isValid,
    );
    
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="space-y-6"
    >
      <Card
        className={cn(
          "border-2 shadow-xl",
          theme === "dark"
            ? "bg-gray-800/50 border-gray-700"
            : "bg-white/80 dark:bg-slate-900/80 border-gray-200 dark:border-slate-700",
        )}
      >
        <CardContent className="p-6">
          {/* Header with title and add button */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500">
                <FileSpreadsheet className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3
                  className={cn(
                    "text-2xl font-bold",
                    theme === "dark" ? "text-white" : "text-gray-900 dark:text-white",
                  )}
                >
                  Bulk Upload
                </h3>
                <p
                  className={cn(
                    "text-sm",
                    theme === "dark" ? "text-gray-400" : "text-gray-600 dark:text-slate-400",
                  )}
                >
                  {rows.length} of {MAX_ROWS} rows
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center">
                <input
                  id="multi-country"
                  type="checkbox"
                  onChange={() => setIsSingleCountry(!isSingleCountry)}
                  className={`h-5 w-5 me-2 cursor-pointer rounded ${
                    theme === "dark"
                      ? "bg-slate-700 border-slate-600 text-blue-500 focus:ring-blue-600"
                      : "bg-white border-slate-300 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
                  }`}
                />
                <Label htmlFor="multi-country" className={cn("mb-0 cursor-pointer", theme === "dark" ? "text-gray-300" : "text-gray-700 dark:text-slate-200")}>
                  Organizations for Single Country
                </Label>
              </div>

              <Button
                onClick={addRow}
                disabled={rows.length >= MAX_ROWS}
                className="bg-gradient-to-r cursor-pointer from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 shadow-md hover:shadow-lg transition-all"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Row
              </Button>
            </div>
          </div>

          {isSingleCountry && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-center gap-4 mb-6 transition-all duration-900">
              {/* Country - with search */}
              <div className="flex items-center gap-1">
                <div className="relative">
                  <Label className={cn("flex items-center gap-1", theme === "dark" ? "text-gray-300" : "text-gray-700 dark:text-slate-200")}>
                    <Globe className="h-3.5 w-3.5" />
                    Country
                  </Label>
                  <SearchableSelect
                    options={countries.map(
                      (c: { iso2: string; name: string }) => ({
                        value: c.iso2,
                        label: c.name,
                      }),
                    )}
                    value={selectedCountry}
                    onChange={setSelectedCountry}
                    placeholder="Select country"
                    theme={theme}
                    inputStyleProp={cn(
                      "w-full overflow-y-visible p-1 px-2 rounded-md border-2 text-base text-sm transition-all shadow-md",
                      theme === "dark"
                        ? "bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                        : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder:text-gray-400"
                    )}
                    listStyleProp={cn(
                      "p-2 text-sm cursor-pointer transition-colors",
                      theme === "dark"
                        ? "bg-gray-800 border-gray-600 text-white"
                        : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white"
                    )}
                  />
                </div>
              </div>

              {/* Currency */}
              <div className="flex items-center gap-1">
                <div className="relative">
                  <Label className={cn("flex items-center gap-1", theme === "dark" ? "text-gray-300" : "text-gray-700 dark:text-slate-200")}>
                    <Banknote className="h-3.5 w-3.5" />
                    Convert to
                  </Label>
                  <SearchableSelect
                    options={currencies}
                    value={selectedCurrency}
                    onChange={(value) => {
                      setSelectedCurrency(value);
                    }}
                    placeholder="Select a currency"
                    theme={theme}
                    inputStyleProp={cn(
                      "w-full overflow-y-visible p-1 px-2 rounded-md border-2 text-base text-sm transition-all shadow-md",
                      theme === "dark"
                        ? "bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                        : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder:text-gray-400"
                    )}
                    listStyleProp={cn(
                      "p-2 text-sm cursor-pointer transition-colors",
                      theme === "dark"
                        ? "bg-gray-800 border-gray-600 text-white"
                        : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white"
                    )}
                  />
                </div>
              </div>

              {/* Date - replaced calendar with dropdown */}
              <div className="flex items-center gap-1">
                <div className="relative">
                  <Label className={cn("flex items-center gap-1", theme === "dark" ? "text-gray-300" : "text-gray-700 dark:text-slate-200")}>
                    <Calendar className="h-3.5 w-3.5" />
                    Date
                  </Label>
                  <SearchableSelect
                    options={currencyRates.map(
                      (c: { id: string; conversion_date: string }) => ({
                        value: c.id,
                        label: c.conversion_date,
                      }),
                    )}
                    value={selectedCurrencyRate}
                    onChange={(value) => {
                      setSelectedCurrencyRate(value);
                    }}
                    placeholder="Select a currency rate"
                    theme={theme}
                    disabled={isDateDisabled(selectedCountry, selectedCurrency, currencies)}
                    inputStyleProp={cn(
                      "w-full overflow-y-visible p-1 px-2 rounded-md border-2 text-base text-sm transition-all shadow-md",
                      theme === "dark"
                        ? "bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                        : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder:text-gray-400"
                    )}
                    listStyleProp={cn(
                      "p-2 text-sm cursor-pointer transition-colors",
                      theme === "dark"
                        ? "bg-gray-800 border-gray-600 text-white"
                        : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white"
                    )}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Table layout */}
          <div>
            <table className="w-full border-collapse">
              {/* Table Header */}
              <thead>
                <tr
                  className={cn(
                    "border-b-2 text-left text-xs font-semibold",
                    theme === "dark"
                      ? "border-gray-700 text-gray-400"
                      : "border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-400",
                  )}
                >
                  <th className="pb-3 pr-4">Report Name *</th>
                  <th className="pb-3 pr-4">Country *</th>
                  <th className="pb-3 pr-4">Organization *</th>
                  <th className="pb-3 pr-4">Convert to *</th>
                  <th className="pb-3 pr-4">Date *</th>
                  <th className="pb-3 pr-4">File *</th>
                  <th className="pb-3 w-10"></th>
                </tr>
              </thead>

              {/* Table Body */}
              <tbody>
                <AnimatePresence>
                  {rows?.map((row) => (
                    <motion.tr
                      key={row.id}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      className={cn(
                        "border-b",
                        theme === "dark"
                          ? "border-gray-700"
                          : "border-gray-200 dark:border-slate-700",
                        !row.isValid &&
                          row.file &&
                          "bg-red-50 dark:bg-red-950/10",
                      )}
                    >
                      {/* Report Name */}
                      <td className="py-3 pr-4">
                        <Input
                          value={row.reportName}
                          onChange={(e) =>
                            updateRowField(row.id, "reportName", e.target.value)
                          }
                          placeholder="Report name"
                          className={cn(
                            "h-9 text-sm",
                            theme === "dark"
                              ? "bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                              : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder:text-gray-400",
                          )}
                        />
                      </td>

                      {/* Country - with search */}
                      <td className="py-3 pr-4">
                        <div className="relative">
                          <SearchableSelect
                            options={countries.map(
                              (c: { iso2: string; name: string }) => ({
                                value: c.iso2,
                                label: c.name,
                              }),
                            )}
                            value={row.country}
                            onChange={(value) => {
                              handleCountryChange(value, row.id);
                            }}
                            disabled={isSingleCountry}
                            placeholder="Select country"
                            theme={theme}
                            inputStyleProp={cn(
                              "w-full overflow-y-visible p-1 px-2 rounded-md border-2 text-base text-sm transition-all shadow-md",
                              theme === "dark"
                                ? "bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                                : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder:text-gray-400"
                            )}
                            listStyleProp={cn(
                              "p-2 text-sm cursor-pointer transition-colors",
                              theme === "dark"
                                ? "bg-gray-800 border-gray-600 text-white"
                                : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white"
                            )}
                          />
                        </div>
                      </td>

                      {/* Bank - with search */}
                      <td className="py-3 pr-4">
                        <div className="relative">
                          <SearchableSelect
                            options={row.banks.map(
                              (c: { value: string; label: string }) => ({
                                value: c.value,
                                label: c.label,
                              }),
                            )}
                            value={row.bank}
                            onChange={(value) => {
                              updateRowField(row.id, "bank", value);
                            }}
                            placeholder="Select bank"
                            theme={theme}
                            inputStyleProp={cn(
                              "w-full overflow-y-visible p-1 px-2 rounded-md border-2 text-base text-sm transition-all shadow-md",
                              theme === "dark"
                                ? "bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                                : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder:text-gray-400"
                            )}
                            listStyleProp={cn(
                              "p-2 text-sm cursor-pointer transition-colors",
                              theme === "dark"
                                ? "bg-gray-800 border-gray-600 text-white"
                                : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white"
                            )}
                          />
                        </div>
                      </td>

                      {/* Currency */}
                      <td className="py-3 pr-4">
                        <div className="relative">
                          <SearchableSelect
                            options={row.currencies?.map(
                              (c: { value: string; label: string }) => ({
                                value: c.value,
                                label: c.label,
                              }),
                            )}
                            value={row.currency}
                            onChange={(value) => {
                              handleCurrencyChange(value, row.country, row.id);
                            }}
                            placeholder="Select a currency"
                            theme={theme}
                            disabled={isSingleCountry}
                            inputStyleProp={cn(
                              "w-full overflow-y-visible p-1 px-2 rounded-md border-2 text-base text-sm transition-all shadow-md",
                              theme === "dark"
                                ? "bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                                : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder:text-gray-400"
                            )}
                            listStyleProp={cn(
                              "p-2 text-sm cursor-pointer transition-colors",
                              theme === "dark"
                                ? "bg-gray-800 border-gray-600 text-white"
                                : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white"
                            )}
                          />
                        </div>
                      </td>

                      {/* Date - replaced calendar with dropdown */}
                      <td className="py-3 pr-4">
                        <SearchableSelect
                          options={row.rates?.map(
                            (c: { value: string; label: string }) => ({
                              value: c.value,
                              label: c.label,
                            }),
                          )}
                          value={row.rate}
                          onChange={(value) => {
                            handleCurrencyRateChange(value, row.id);
                          }}
                          placeholder="Select a currency rate"
                          theme={theme}
                          disabled={
                            isSingleCountry ||
                            isDateDisabled(row.country, row.currency, row.currencies)
                          }
                          inputStyleProp={cn(
                            "w-full overflow-y-visible p-1 px-2 rounded-md border-2 text-base text-sm transition-all shadow-md",
                            theme === "dark"
                              ? "bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                              : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder:text-gray-400"
                          )}
                          listStyleProp={cn(
                            "p-2 text-sm cursor-pointer transition-colors",
                            theme === "dark"
                              ? "bg-gray-800 border-gray-600 text-white"
                              : "bg-white dark:bg-slate-900 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white"
                          )}
                        />
                      </td>

                      {/* File */}
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <input
                            ref={(el) => {
                              if (el) fileInputRefs.current.set(row.id, el);
                              else fileInputRefs.current.delete(row.id);
                            }}
                            type="file"
                            accept=".xlsx"
                            onChange={(e) =>
                              handleFileSelect(
                                row.id,
                                e.target.files?.[0] || null,
                              )
                            }
                            className="hidden"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => triggerFileInput(row.id)}
                            className={cn(
                              "flex-1 cursor-pointer h-9 text-sm justify-start overflow-hidden",
                              theme === "dark"
                                ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                                : "border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-900",
                            )}
                          >
                            <Upload className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span className="truncate">
                              {row.file ? row.file.name : "Choose file"}
                            </span>
                          </Button>
                          {row.file &&
                            (row.isValid ? (
                              <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0" />
                            ) : (
                              <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0" />
                            ))}
                        </div>
                        {row.validationError && (
                          <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                            {row.validationError}
                          </p>
                        )}
                      </td>

                      {/* Remove button */}
                      <td className="py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRow(row.id)}
                          className={cn(
                            "h-8 cursor-pointer w-8 p-0",
                            theme === "dark"
                              ? "text-gray-400 hover:text-red-400 hover:bg-red-900/20"
                              : "text-gray-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30",
                          )}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* No rows message */}
          {rows.length === 0 && (
            <div
              className={cn(
                "text-center py-12 border-2 border-dashed rounded-xl mt-4",
                theme === "dark" ? "border-gray-700" : "border-gray-300 dark:border-slate-600",
              )}
            >
              <FileSpreadsheet
                className={cn(
                  "w-12 h-12 mx-auto mb-3",
                  theme === "dark" ? "text-gray-600" : "text-gray-400",
                )}
              />
              <p
                className={cn(
                  "text-sm",
                  theme === "dark" ? "text-gray-400" : "text-gray-600 dark:text-slate-400",
                )}
              >
                No rows added. Click "Add Row" to start.
              </p>
            </div>
          )}

          {/* Submit section */}
          {rows.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 pt-6 border-t"
            >
              {(error || validationError) && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {error || validationError}
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRows([])}
                  disabled={isProcessing}
                  className={cn(
                    "flex-1 cursor-pointer order-2 sm:order-1",
                    theme === "dark"
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white"
                      : "border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-900"
                  )}
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear All
                </Button>

                <Button
                  onClick={handleSubmit}
                  disabled={!allRowsValid || isProcessing}
                  className={cn(
                    "flex-[2] cursor-pointer order-1 sm:order-2 font-bold text-base flex items-center justify-center gap-2",
                    "bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white",
                    "hover:from-indigo-500 hover:via-purple-500 hover:to-pink-500",
                    "disabled:opacity-50 shadow-lg hover:shadow-xl transition-all",
                  )}
                >
                  {isProcessing ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      >
                        <Zap className="w-5 h-5" />
                      </motion.div>
                      Processing {rows.length} files...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Start Bulk Analysis (
                      {rows.filter((r) => r.file && r.isValid).length} files)
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
